import type { FontFamily } from '../../types';

const STIX_TWO_MATH_FAMILY = 'STIX Two Math';

const serializeCssString = (value: string): string =>
  Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint === 0) return '\uFFFD';
    if (character === '"' || character === '\\') return `\\${character}`;
    if ((codePoint >= 1 && codePoint <= 31) || codePoint === 127) {
      return `\\${codePoint.toString(16)} `;
    }
    return character;
  }).join('');

const quoteFontFamily = (value: string): string =>
  `"${serializeCssString(value)}"`;

export const generateFontFamily = (
  { family, fallbacks }: { family?: FontFamily; fallbacks?: string[] } = {},
  renderFamily?: string
) => {
  const genericFamilyKeywords = [
    'cursive',
    'fantasy',
    'monospace',
    'serif',
    'sans-serif',
  ];

  const familiesAndFallbacks = [`"Noto Color Emoji"`];

  const effectiveFamily = renderFamily || family;

  if (effectiveFamily) {
    // Wrap into " since some fonts won't work without it.
    familiesAndFallbacks.push(quoteFontFamily(effectiveFamily));
  }

  if (fallbacks?.length) {
    const sortedFallbacks = [...fallbacks].sort((fallbackA, fallbackB) => {
      if (genericFamilyKeywords.includes(fallbackA)) {
        return 1;
      }

      if (genericFamilyKeywords.includes(fallbackB)) {
        return -1;
      }

      return 0;
    });

    const stixTwoMathIndex = sortedFallbacks.findIndex(
      (fallback) => fallback === STIX_TWO_MATH_FAMILY
    );

    if (stixTwoMathIndex === -1 && effectiveFamily !== STIX_TWO_MATH_FAMILY) {
      const firstGenericFallbackIndex = sortedFallbacks.findIndex((fallback) =>
        genericFamilyKeywords.includes(fallback)
      );

      // Insert STIX Two Math before the generic fallbacks
      if (firstGenericFallbackIndex !== -1) {
        sortedFallbacks.splice(
          firstGenericFallbackIndex,
          0,
          STIX_TWO_MATH_FAMILY
        );
      } else {
        sortedFallbacks.push(STIX_TWO_MATH_FAMILY);
      }
    }

    for (const fallback of sortedFallbacks) {
      familiesAndFallbacks.push(
        genericFamilyKeywords.includes(fallback)
          ? fallback
          : quoteFontFamily(fallback)
      );
    }
  } else if (effectiveFamily !== STIX_TWO_MATH_FAMILY) {
    familiesAndFallbacks.push(`"${STIX_TWO_MATH_FAMILY}"`);
  }

  return familiesAndFallbacks.join(', ');
};
